import type { LogsSearchQuery, LogSearch } from '~/generated/graphql';

export type LogsSearchResult = LogsSearchQuery;

export type LogLevel = 'ALL SEVERITIES' | 'INFO' | 'ERROR' | 'WARN';
export type LogType = 'ALL SOURCES' | 'INTERNAL' | 'LOG_CONNECTOR';
export type SortDirection = 'ASC' | 'DESC';

/**
 * @deprecated Use PipelineLogOtel for new implementations with Monitor Service REST API.
 * This type is maintained for backward compatibility with GraphQL-based log fetching.
 * TODO: REMOVE AFTER monitor_migrate_pipeline_logs_to_monitor_service IS PERMANENT - Entire type definition
 */
type GeneratedLog = NonNullable<
  NonNullable<LogsSearchQuery['logsSearch']>['logs']
>[number];

export type PipelineLogBase = NonNullable<GeneratedLog> & {
  id: string;
  key: string;
  pipelineName: string;
  pipelineKey: string;
  logMessage: string;
  timestamp: string;
  timestampMillis: string;
  realm: string;
  logLevel: string;
};

export type PipelineLogsRequest = {
  realm: string;
  environment: string;
  filters: {
    pipelineKey?: string;
    pipelineName?: string;
    logMessage?: string;
    pipelineVersion?: string;
    logType?: string;
    level?: string;
  };
  timeRange: {
    from: string;
    to: string;
  };
  pagination: {
    limit: number;
    order: 'ascending' | 'descending';
    cursor?: string;
  };
};

export type PipelineLogAttributes = {
  'log.id': string;
  'log.type'?: string;
  'pipeline.name': string;
  'pipeline.key': string;
  'pipeline.version': string;
};

export type PipelineLogResource = {
  'service.name': string;
};

export type PipelineLogsResponse = {
  data: PipelineLogItem[];
  pagination: {
    limit: number;
    nextCursor?: string;
    hasNext: boolean;
  };
};

export type PipelineLogItem = {
  body: string;
  severityText: string;
  severityNumber: number;
  timestamp: string;
  observedTimestamp: string;
  attributes: PipelineLogAttributes;
  resource: PipelineLogResource;
  traceId: string | null;
  spanId: string | null;
};

export type PipelineLogOtel = {
  id: string;
  key: string;
  pipelineName: string;
  pipelineKey: string;
  pipelineVersion: string;
  logMessage: string;
  logType?: string;
  timestamp: string;
  observedTimestamp: string;
  severityText: string;
  severityNumber: number;
  serviceName: string;
  traceId: string | null;
  spanId: string | null;
};

// TODO: REMOVE AFTER monitor_migrate_pipeline_logs_to_monitor_service IS PERMANENT - Entire type definition
export type PipelineLog = PipelineLogBase & {
  key: string;
};

export type SearchParams = {
  pipelineName: string | null;
  pipelineVersion: string | null;
  time: string;
  logMessage: string | null;
  pipelineKey: string | null;
  level: string | null;
  logType: string | null;
  direction: string;
  specific?: {
    dateFrom: Date;
    dateTo: Date;
  } | null;
  dateFrom?: string | null;
  dateTo?: string | null;
};

export type UsePipelineLogsDataParams = {
  realm: string;
  environment: string;
  searchParams: SearchParams;
  limit?: number;
};

export type PipelineLogsState = {
  logs: PipelineLog[] | PipelineLogOtel[];
  count: number;
  loading: boolean;
  error: Error | null;
};

// TODO: REMOVE AFTER monitor_migrate_pipeline_logs_to_monitor_service IS PERMANENT - All code below
export function isOTELLog(log: unknown): log is PipelineLogOtel {
  return (
    typeof log === 'object' &&
    log !== null &&
    'severityText' in log &&
    'observedTimestamp' in log &&
    'serviceName' in log
  );
}

// TODO: REMOVE AFTER monitor_migrate_pipeline_logs_to_monitor_service IS PERMANENT - Transform OTEL to GraphQL format
export function mapOtelToLogSearch(otelLog: PipelineLogOtel): LogSearch {
  return {
    __typename: 'LogSearch',
    id: otelLog.id,
    logLevel: otelLog.severityText,
    logMessage: otelLog.logMessage,
    pipelineKey: otelLog.pipelineKey,
    pipelineName: otelLog.pipelineName,
    realm: undefined,
    timestamp: otelLog.timestamp,
    timestampMillis: otelLog.timestamp
  };
}
